import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CustomerDataArray, CustomerItem, CustomerModel, CustomerQueryParams } from '@app/interfaces/customer';
import { getCustomersApi, createCustomerApi, updateCustomerApi, deleteCustomerApi, getCustomerFiltersApi, getCustomerApi, assignCustomerApi, recalculateCustomerDebitApi } from '@app/api/customer.api';
import { message } from 'antd';
import { showError } from '@app/utils/message';

export interface CustomerState {
  data: CustomerModel[];
  totalRows: number;
    pagination: {
      current: number,
      pageSize: number
    },
    filters: any,
    sorter: any,
    searchTerm: string;
    selectedRecord: CustomerItem[];
    loading: boolean;
    error: string | null;
    filterData: {
      customerCode: string[];
      customerName: string[]; 
    };
    record: CustomerModel | null;
}

const initialState: CustomerState = {
  data: [],
  totalRows: 0,
  pagination: { current: 1, pageSize: 20 },
  filters: {},
  sorter: {},
  searchTerm: "",
  selectedRecord: [],
  loading: false,
  error: null,
  filterData: {
    customerCode: [],
    customerName: [], 
  },
  record: null,
};

interface ResponseData {
  data: CustomerItem;
  message: string;
}

export interface FilterResponse {
  customer_code: string[];
  name: string[];
}

// Thunks
export const getCustomers = createAsyncThunk<CustomerDataArray, CustomerQueryParams>(
  'customer/getCustomers',
  async (params: CustomerQueryParams, { rejectWithValue }) => {
    try {
      const customers = (await getCustomersApi(params)) as CustomerDataArray;
      return customers || [];
    } catch (error: any) {
      return rejectWithValue(error?.error || 'An error occurred');
    }
  },
);

export const getCustomer = createAsyncThunk<CustomerModel, number>(
  'customer/getCustomer',
  async (customerId, { rejectWithValue }) => {
    try {
      const data = await getCustomerApi(customerId);
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred'
      );
    }
  }
);

export const createCustomer = createAsyncThunk<ResponseData, CustomerModel | FormData>(
  'customer/createCustomer',async (data) => {
  const response = await createCustomerApi(data);
  return response;
});

export const updateCustomer = createAsyncThunk<ResponseData, { customerId: number; formData: CustomerModel | FormData }>(
  'customer/updateCustomer',
  async ({ customerId, formData }) => {
    const response = await updateCustomerApi(customerId, formData);
    return response;
  },
);

export const deleteCustomer = createAsyncThunk<{ customerId: number; response: any }, number>(
  'customer/deleteCustomer',
  async (customerId, { rejectWithValue }) => {
    try {
      const response = await deleteCustomerApi(customerId);
      return { customerId, response };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Có lỗi xảy ra khi xoá khách hàng');
    }
  },
);

export const getFilterCustomer = createAsyncThunk<FilterResponse, Partial<{fields: string}>>(
  'customer/getFilterCustomer',
  async (params) => {
    try {
      const response = await getCustomerFiltersApi({params});
      return response;
    } catch (error) {
      throw new Error('Failed to fetch customer filters');
    }
  },
);

export const recalculateCustomerDebit = createAsyncThunk<ResponseData, number>(
  'customer/recalculateDebit',
  async (customerId, { rejectWithValue }) => {
    try {
      const response = await recalculateCustomerDebitApi(customerId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.error || 'Có lỗi xảy ra');
    }
  },
);

export const assignCustomer = createAsyncThunk<ResponseData, { customerId: number; formData: any }>(
  'customer/assignCustomer',
  async ({ customerId, formData }) => {
    const response = await assignCustomerApi(customerId, formData);
    return response;
  },
);

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

// Slice
export const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    updateTableParams(state, action) {
      const { pagination, filters, sorter, searchTerm } = action.payload;
      if (pagination !== undefined) state.pagination = pagination;
      if (filters !== undefined) state.filters = filters;
      if (sorter !== undefined) state.sorter = sorter;
      if (searchTerm !== undefined) state.searchTerm = searchTerm;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCustomers.fulfilled, (state, action) => {
        console.log('Fulfilled payload:', action.payload);
        state.loading = false;

        const data = action.payload.rows;
        state.data = data.map((item: CustomerItem) => item.attributes) || [];
        const totalRows = (action.payload)?.total_rows;
        state.totalRows = typeof totalRows === 'string' ? parseInt(totalRows, 10) : (totalRows ?? 0);
      })
      .addCase(getCustomer.fulfilled, (state, action) => {
        const customer = action.payload.data;
        state.record = customer.attributes;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        state.data.unshift(data.attributes);
        message.success(newMessage);
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        const dataId = typeof data.id === 'string' ? parseInt(data.id, 10) : data.id;

        const updatedIndex = state.data.findIndex((item) => {
          const itemId = typeof item.id === 'string' ? parseInt(item.id, 10) : item.id;
          return itemId === dataId;
        });

        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data.attributes;
          state.record = data.attributes;
          message.success(newMessage);
        } else {
          // If not found in existing data, add it to the list (new record)
          state.data.unshift(data.attributes);
          state.record = data.attributes;
          message.success(newMessage);
        }
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        const { customerId, response } = action.payload;

        if (response.error) { 
          showError(response.error); return;
        }
        else {
          state.data = state.data.filter((customer) => customer.id !== customerId);
          message.success(response.message);
        }
      })
      .addCase(getFilterCustomer.fulfilled, (state, action) => {
        const { customer_code, name } = action.payload;

        state.filterData = {
          customerCode: customer_code || [],
          customerName: name || [],  
        };
      })
      .addCase(assignCustomer.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        const dataId = typeof data.id === 'string' ? parseInt(data.id, 10) : data.id;

        const updatedIndex = state.data.findIndex((item) => item.id === dataId);
        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data.attributes;
          state.record = data.attributes;
          message.success(newMessage);
        }
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
  
        // Check if the error response contains a message
        const errorMessage =
          action.payload?.message || // API error message
          action.payload?.error ||  // Custom error structure
          action?.error?.message || // Default error message
          'An error occurred';

        // Handle 403 Forbidden separately
        if (action.meta?.rejectedWithValue && action.error?.code === 403) {
          showError('You are not authorized to perform this action.');
        } else {
          showError(errorMessage);
        }
      });
  },
});

export const { updateTableParams } = customerSlice.actions;

export default customerSlice.reducer;
